import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { AvailableProductLine } from '../../../Models/Enums';
import { Constants } from '../../../Models/Constants';
import { getPHEType, getPositionAndProductLineFromConfigurationKey } from '../../../Utils/UnitConfigurationHelper';
import { PositionBadge } from '../../Atom/PositionBadge/PositionBadge';
import { Column } from './PHEPlateTypeManagementTypes';
import { PHEPlateTypeManagementStyles, pheChipStyles, actionButtonStyles, layoutStyles } from './PHEPlateTypeManagementStyles';
import { createFlexStyles } from '../../../theme/layoutStyles';

/**
 * Props for table columns creation
 */
interface TableColumnsProps {
  disabledForPositions: any[];
  isDeleting: string;
  editPHEtype: (item: IPHEPlateType) => void;
  setShowDeleteConfirmation: (key: string) => void;
}

/**
 * Create table columns configuration with JSX formatters
 */
export const createTableColumns = ({
  disabledForPositions,
  isDeleting,
  editPHEtype,
  setShowDeleteConfirmation
}: TableColumnsProps): Column[] => {
  return [
    {
      id: 'text',
      label: 'Display name',
      minWidth: 200,
      maxWidth: 200,
      sortable: true,
      format: (value, item) => (
        <Tooltip title={item?.text || ''}>
          <Typography variant="body2" component="code" sx={{ p: 0.5 }}>
            {item?.text}
          </Typography>
        </Tooltip>
      )
    },
    {
      id: 'key',
      label: 'Key',
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      format: (value, item) => (
        <Tooltip title={item?.key || ''}>
          <Typography variant="body2" component="code" sx={{ p: 0.5 }}>
            {item?.key}
          </Typography>
        </Tooltip>
      )
    },
    {
      id: 'isAvailableInALICE',
      label: 'Available in ALICE',
      minWidth: 150,
      maxWidth: 150,
      align: 'center',
      sortable: true,
      format: (value) => {
        return value ? 
          <CheckIcon sx={{ color: 'success.main' }} /> : 
          <CancelIcon sx={{ color: 'error.main' }} />;
      }
    },
    {
      id: 'availableProductLine',
      label: 'Alice Configurator',
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      format: (value) => {
        if (value === null || value === undefined) return null;
        
        if (value === AvailableProductLine.Hygienic) {
          return (
            <Chip 
              label="Hygienic" 
              variant="outlined" 
              size="small"
              sx={pheChipStyles.hygienic}
            />
          );
        } else {
          return (
            <Chip 
              label="Industrial" 
              variant="outlined" 
              size="small"
              sx={pheChipStyles.industrial}
            />
          );
        }
      }
    },
    {
      id: 'disabledFor',
      label: 'Disabled for',
      minWidth: 180,
      maxWidth: 180,
      format: (value: any, item?: IPHEPlateType, index?: number) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {index !== undefined && disabledForPositions[index] && disabledForPositions[index]
            .filter((x: any) => !x.isAvailable)
            .map((x: any, i: number) => (
              <Chip
                key={`${x.alfaLavalPosition}-${x.productLine}-${i}`}
                label={`${x.productLine.substring(0, 3)} | ${x.alfaLavalPosition}`}
                size="small"
                variant="outlined"
              />
            ))
          }
        </Box>
      )
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      align: 'center',
      sortable: false,
      format: (value: any, item?: IPHEPlateType) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => item && editPHEtype(item)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={isDeleting === item?.key}
                onClick={() => item && setShowDeleteConfirmation(item.key!)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];
};

/**
 * Action buttons component
 */
interface ActionButtonsProps {
  onCreateNew: () => void;
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  isLoading: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  onCreateNew,
  searchTerm,
  onSearchChange,
  onClearSearch,
  isLoading
}) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      width: '100%', 
      mb: 2 
    }}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onCreateNew}
        disabled={isLoading}
        sx={{ 
          whiteSpace: 'nowrap', 
          padding: '6px 26px !important',
          textTransform: 'none',
          boxShadow: 1
        }}
      >
        Create new PHE plate type
      </Button>
      
      <TextField
        placeholder="Search by name or key..."
        size="small"
        value={searchTerm}
        onChange={onSearchChange}
        sx={{ width: '300px', height: '36px' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={onClearSearch}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </Box>
  );
};

/**
 * Loading component
 */
interface LoadingComponentProps {
  styles: PHEPlateTypeManagementStyles;
}

export const LoadingComponent: React.FC<LoadingComponentProps> = ({ styles }) => {
  return (
    <Box sx={styles.loadingContainer}>
      <CircularProgress size={40} />
      <Typography variant="body1" ml={2}>
        Loading...
      </Typography>
    </Box>
  );
};

/**
 * No results message component
 */
interface NoResultsMessageProps {
  styles: PHEPlateTypeManagementStyles;
}

export const NoResultsMessage: React.FC<NoResultsMessageProps> = ({ styles }) => {
  return (
    <Paper sx={styles.noResultsMessage}>
      <Typography variant="body1" color="text.secondary">
        No PHE plate types match your search criteria
      </Typography>
    </Paper>
  );
};

/**
 * Position chip component
 */
interface PositionChipProps {
  position: string;
  index: number;
  onClick: () => void;
  theme: any;
}

export const renderPositionChip = ({ position, index, onClick, theme }: PositionChipProps) => {
  const posInfo = getPositionAndProductLineFromConfigurationKey(position);
  
  return (
    <PositionBadge
      key={`${position}-${index}`}
      onClick={onClick}
      type={posInfo.productLine}
      name={posInfo.position}
      background={theme.palette.primary.main} 
      fontColor='white'
    />
  );
};

/**
 * Data table component
 */
interface DataTableProps {
  sortedData: IPHEPlateType[];
  dataColumns: Column[];
  styles: PHEPlateTypeManagementStyles;
  order: 'asc' | 'desc';
  orderBy: string;
  onRequestSort: (property: string) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  sortedData,
  dataColumns,
  styles,
  order,
  orderBy,
  onRequestSort
}) => {
  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table stickyHeader aria-label="PHE plate types table" sx={styles.table}>
        <TableHead>
          <TableRow>
            {dataColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                style={{ 
                  width: column.minWidth,
                  maxWidth: column.maxWidth,
                }}
                sx={styles.headerCell}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.sortable !== false ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => onRequestSort(column.id)}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                ) : (
                  <Typography variant="body2" fontWeight="bold">
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((item, index) => (
            <TableRow
              hover
              key={`${item.key}-${index}`}
              sx={styles.tableRow}
            >
              {dataColumns.map((column) => (
                <TableCell 
                  key={column.id} 
                  align={column.align || 'left'}
                  style={{
                    width: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                  sx={styles.tableCell}
                >
                  {column.format 
                    ? column.format(item[column.id as keyof IPHEPlateType], item, index) 
                    : item[column.id as keyof IPHEPlateType]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/**
 * Form dialog component
 */
interface FormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pheTypeActive?: IPHEPlateType;
  selectedPositions: string[];
  availablePositions: string[];
  text: string;
  itemKey: string;
  availableInALICE: boolean;
  industrial: boolean;
  hygienic: boolean;
  infoForALICE: string;
  isSubmitDisabled: boolean;
  isSaving: boolean;
  styles: PHEPlateTypeManagementStyles;
  onTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAvailableInALICEChange: () => void;
  onIndustrialChange: () => void;
  onHygienicChange: () => void;
  onInfoForALICEChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPositionSelection: (item: string, selected: boolean) => void;
  onSubmit: () => void;
}

export const FormDialog: React.FC<FormDialogProps> = ({
  isOpen,
  onClose,
  pheTypeActive,
  selectedPositions,
  availablePositions,
  text,
  itemKey,
  availableInALICE,
  industrial,
  hygienic,
  infoForALICE,
  isSubmitDisabled,
  isSaving,
  styles,
  onTextChange,
  onKeyChange,
  onAvailableInALICEChange,
  onIndustrialChange,
  onHygienicChange,
  onInfoForALICEChange,
  onPositionSelection,
  onSubmit
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h6">
          {pheTypeActive !== undefined 
            ? `Edit ${pheTypeActive.key} PHE type` 
            : 'New PHE Plate type'}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={2}>
          <Box sx={styles.formSection}>
            <Typography variant="subtitle1" sx={styles.formLabel}>
              PHE type <Typography component="code" variant="body2">disabled</Typography> for
            </Typography>
            <Box sx={styles.positionsContainer}>
              {selectedPositions.length > 0 ? (
                selectedPositions.map((pos, index) => 
                  renderPositionChip({ 
                    position: pos, 
                    index, 
                    onClick: () => !isSaving && onPositionSelection(pos, false),
                    theme 
                  })
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  None selected
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box sx={styles.formSection}>
            <Typography variant="subtitle1" sx={styles.formLabel}>
              PHE type selectable for
            </Typography>
            <Box sx={styles.positionsContainer}>
              {availablePositions.map((pos, index) => 
                renderPositionChip({ 
                  position: pos, 
                  index, 
                  onClick: () => !isSaving && onPositionSelection(pos, true),
                  theme 
                })
              )}
            </Box>
          </Box>
          
          <Stack direction={layoutStyles.formRow.direction} spacing={layoutStyles.formRow.spacing} sx={styles.formSection}>
            <TextField
              fullWidth
              required
              label="Text (display name)"
              value={text}
              onChange={onTextChange}
              disabled={isSaving}
            />
            
            <TextField
              fullWidth
              required
              label="Key (database key)"
              value={itemKey}
              onChange={onKeyChange}
              disabled={pheTypeActive !== undefined || isSaving}
            />
          </Stack>
          
          <Box sx={styles.formSection}>
            <Stack direction={layoutStyles.checkboxRow.direction} justifyContent={layoutStyles.checkboxRow.justifyContent} alignItems={layoutStyles.checkboxRow.alignItems}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={availableInALICE} 
                    onChange={onAvailableInALICEChange}
                    disabled={isSaving}
                  />
                }
                label="Is available in ALICE?"
              />
              
              <Stack direction={layoutStyles.checkboxGroup.direction} spacing={layoutStyles.checkboxGroup.spacing}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={industrial} 
                      onChange={onIndustrialChange}
                      disabled={isSaving}
                    />
                  }
                  label="Is Industrial"
                />
                
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={hygienic} 
                      onChange={onHygienicChange}
                      disabled={isSaving}
                    />
                  }
                  label="Is Hygienic"
                />
              </Stack>
            </Stack>
            
            {!availableInALICE && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="General message for user"
                  value={infoForALICE}
                  onChange={onInfoForALICEChange}
                  disabled={isSaving}
                />
                
                <Typography variant="subtitle2" mt={1}>
                  Default message
                </Typography>
                
                <Typography variant="body2" sx={styles.infoMessage}>
                  {Constants.XMLCopyDefaultMessage.replace(
                    '{pheType}', 
                    getPHEType(pheTypeActive?.text) ?? text
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={actionButtonStyles.dialogActions}>
        {isSaving ? (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={24} />
            <Typography ml={2}>
              {pheTypeActive !== undefined ? 'Updating PHE plate type...' : 'Creating PHE plate type...'}
            </Typography>
          </Box>
        ) : (
          <>
            <Button 
              onClick={onClose} 
              color="inherit"
              disabled={isSaving}
            >
              Cancel
            </Button>
            
            <Button 
              onClick={onSubmit} 
              variant="contained" 
              color="primary"
              startIcon={<CheckIcon />}
              disabled={isSubmitDisabled || isSaving}
            >
              Submit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete confirmation dialog component
 */
interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  itemKey: string;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  isOpen,
  itemKey,
  isDeleting,
  onClose,
  onConfirm
}) => {
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>
        Delete PHE Type "{itemKey}"
      </DialogTitle>
      
      <DialogContent>
        <Typography>
          Are you sure you want to delete PHE type {itemKey}? This action cannot be undone.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        {isDeleting ? (
          <Box sx={{ ...flexStyles.flexCenter, width: '100%', p: 2 }}>
            <CircularProgress size={30} />
            <Typography ml={2}>Deleting PHE type...</Typography>
          </Box>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={onConfirm} 
              variant="contained" 
              color="error"
            >
              Delete
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}; 